import React, { useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import {  Edit, User, Calendar, Eye, EyeOff, Plus, Trash2, BarChart3, Swords, Star, Users, Sparkles, Zap, Trophy, ScrollText, BadgeCheck, Shield, Box } from 'lucide-react';
import { DeckVotes } from '../../components/decks/DeckVotes';
import { useAuth } from '../../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useCollection } from '../../hooks/useCollection';
import { SUPABASE_IMAGE_BASE_URL, TINYMCE_API_KEY } from '../../lib/constants';
import { DeleteConfirmation } from '../../components/common/DeleteConfirmation';
import { useDecks } from '../../hooks/useDecks';
import { StarRating } from '../../components/common/StarRating';
import { CommentSection } from '../../components/comments/CommentSection';
import { supabase } from '../../lib/supabase';
import { PageLayout } from '../../components/layout/PageLayout';
import { useQueries } from '../../hooks/useQueries';
import { DeckDetailSkeleton } from '../../components/skeletons/DeckDetailSkeleton';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';
import { translateEnergyType } from '../../utils/energyUtils';

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
  img?: string;
  release_date?: string;
}

export function DeckDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { collection } = useCollection();
  const { deleteDeck } = useDecks();
  const { t, lang, getPathForLang } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [comments, setComments] = useState<any[]>([]);

  const [{ data: deck, loading, error }, { data: sets }] = useQueries([
    { 
      key: 'decks',
      params: { id },
      options: { 
      single: true,
      withRelations: `
        *,
        profiles:user_id (
          username,
          avatar_url
        ),
          cards:deck_cards(
            quantity, 
            card:cards(*)
          ),
        ratings:deck_ratings(
          upvotes,
          downvotes,
          total_votes,
          rating_score
        )
      ` 
    }
    },
    {
      key: 'sets',
      options: {
        single: false
      }
    }
  ]);

  const getSetInfo = (setId: string): SetInfo | null => {
    if (!setId) return null;
    // Comparer avec le name du set au lieu de l'id
    return sets?.find(set => String(set.name) === String(setId)) as SetInfo | null;
  };

  // Déplacer ces calculs avant l'effet SEO
  const isOwner = user?.id === deck?.user_id;
  const rating = deck?.ratings?.[0]?.rating_score || 0;
  const totalVotes = deck?.ratings?.[0]?.total_votes || 0;
  const totalCards = deck?.cards?.reduce((sum, card) => sum + card.quantity, 0) || 0;

  // Effet SEO après les calculs
  React.useEffect(() => {
    if (deck) {
      document.title = `${deck.name} - Deck Pokémon par ${deck.profiles.username}`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          `Découvrez le deck Pokémon "${deck.name}" créé par ${deck.profiles.username}. ` +
          `Un deck ${deck.is_public ? 'public' : 'privé'} contenant ${totalCards} cartes. ` +
          `Note moyenne: ${(rating / 20).toFixed(1)}/5 basée sur ${totalVotes} votes.`
        );
      }
    }
  }, [deck, totalCards, rating, totalVotes]);

  const fetchComments = useCallback(async () => {
    const { data } = await supabase
      .from('deck_comments')
      .select(`
        *,
        profiles!deck_comments_user_id_fkey1(
          username,
          avatar_url
        )
      `)
      .eq('deck_id', id)
      .order('created_at', { ascending: true });

    if (data) {
      const formattedComments = data.map(comment => ({
        ...comment,
        profile: comment.profiles
      }));
      setComments(formattedComments);
    }
  }, [id]);

  React.useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleDelete = async () => {
    if (!deck) return;
    try {
      setIsDeleting(true);
      await deleteDeck(deck.id);
      navigate('/decks');
    } catch (error) {
      console.error('Error deleting deck:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('deck_detail.loading'), path: '' }
          ]
        }}
        className="py-8"
      >
        <DeckDetailSkeleton />
      </PageLayout>
    );
  }

  if (error || !deck) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{t('deck_detail.error_loading')}</p>
      </div>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: isOwner ? t('deck_detail.my_decks') : t('deck_detail.public_decks'), path: getPathForLang(isOwner ? '/decks' : '/public-decks') },
          { label: deck.name, path: getPathForLang(`/decks/${deck.id}`) }
        ]
      }}
      className="py-8"
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
      {/* En-tête du Deck */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1">
                <div className="flex items-center gap-3">
                <Link 
                  to={getPathForLang(`/users/${deck.profiles?.username}`)}
                  className="hover:opacity-80 transition-opacity max-w-[200px]"
                >
                  {deck.profiles.avatar_url ? (
                    <img 
                      src={deck.profiles.avatar_url} 
                      alt={`${deck.profiles.username}'s avatar`}
                        className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 text-lg">
                        {deck.profiles.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  </Link>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-1">{deck.name}</h1>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-gray-600">
                      <Link 
                        to={getPathForLang(`/users/${deck.profiles?.username}`)}
                        className="flex items-center gap-1 sm:gap-2 hover:text-indigo-600 transition-colors group min-w-0"
                      >
                        <User className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate max-w-[80px] sm:max-w-[120px] group-hover:underline" title={deck.profiles.username}>
                          {deck.profiles.username}
                        </span>
                      </Link>
                      <span className="flex items-center gap-1 sm:gap-2">
                        <Calendar className="w-4 h-4" />
                        {formatDistanceToNow(new Date(deck.updated_at), { addSuffix: true, locale: lang === 'fr' ? fr : enUS })}
                      </span>
                      <span className="flex items-center gap-1 sm:gap-2">
                        {deck.is_public ? (
                          <Eye className="w-4 h-4 text-green-500" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        )}
                        {deck.is_public ? t('deck_detail.public') : t('deck_detail.private')}
                      </span>
                    </div>
                  </div>
              </div>
            </div>
            
            {isOwner && (
              <div className="flex items-center gap-2">
                <Link
                  to={getPathForLang(`/decks/${deck.id}/edit`)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t('deck_detail.edit')}
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('deck_detail.delete')}
                </button>
              </div>
            )}
          </div>

          {/* Description */}
          {deck.description && (
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h2 className="text-sm font-medium text-gray-700 mb-2">{t('deck_detail.description')}</h2>
              <div 
                className="prose prose-sm max-w-none text-gray-600"
                dangerouslySetInnerHTML={{ __html: deck.description }}
              />
            </div>
          )}
        </div>

        {/* Annonce AdSense unique */}
        <div className="mt-6 mb-8 flex justify-center items-center">
          <AdSense adSlot="1424794127" id="deck-details" />
        </div>

        {/* Section Votes */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-indigo-100">
          <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600">
            <h3 className="text-lg font-medium text-white px-6 py-4">
              {t('deck_detail.community_votes')}
            </h3>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
            <div className="p-6 flex-1 flex flex-col items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm font-medium text-gray-500">{t('deck_detail.average_rating')}</p>
                <div className="text-3xl font-bold text-gray-900">
                  {(rating / 20).toFixed(1)}/5
                </div>
                <StarRating 
                  rating={rating} 
                  totalVotes={totalVotes}
                  showVoteCount={false}
                  className="text-lg justify-center"
                />
                <p className="text-sm text-gray-500">
                  {totalVotes} {t('deck_detail.votes', { count: totalVotes })}
                </p>
              </div>
            </div>
            
            <div className="p-6 flex-1 flex flex-col items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <p className="text-sm font-medium text-gray-500 mb-2">{t('deck_detail.vote_for_deck')}</p>
                <DeckVotes deckId={deck.id} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des Cartes */}
      <div className="mt-8">
        {deck.cards && deck.cards.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-md p-8 border border-indigo-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('deck_detail.deck_cards')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
              {deck.cards.map((card) => {
                const isInCollection = collection[card.card?.id]?.quantity > 0;
                const imageUrl = card.card?.image ? 
                  `${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/preview/' : 'preview/'}${card.card.image}` : null;
                
                return (
                  <div key={card.card?.id} className="relative group">
                    {imageUrl && (
                      <Link
                        to={getPathForLang(`/sets/${card.card?.set}/cards/${card.card?.id}`)}
                        className="flex flex-col"
                      >
                        <div className="relative aspect-[3/4]">
                          <img
                            src={imageUrl}
                            alt={card.card?.[lang === 'en' ? 'card_en' : 'card_fr'] || t('deck_detail.pokemon_card')}
                            className={`w-full h-full object-contain rounded-lg transition-transform duration-200 group-hover:scale-105 ${
                              !isInCollection ? 'grayscale' : ''
                            }`}
                          />
                          <div className="absolute top-2 right-2 flex gap-2">
                            <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-medium">
                              {card.card?.rarity}
                            </span>
                            {card.quantity > 1 && (
                              <div className="bg-indigo-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                                ×{card.quantity}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 text-center">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {card.card?.[lang === 'en' ? 'card_en' : 'card_fr']}
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
            <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-indigo-100">
              <p className="text-gray-500 mb-4">{t('deck_detail.no_cards')}</p>
            {isOwner && (
              <Link
                to={getPathForLang(`/decks/${deck.id}/edit`)}
                className="inline-flex items-center px-6 py-3 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('deck_detail.add_cards')}
              </Link>
            )}
          </div>
        )}
      </div>

        {/* Section des commentaires - Utiliser directement le composant */}
        <CommentSection
          resourceId={deck.id}
          resourceType="deck"
          comments={comments}
          onCommentAdded={fetchComments}
          apiKey={TINYMCE_API_KEY}
          className="bg-white rounded-2xl shadow-md border border-indigo-100"
        />

        {/* Section Analyse */}
        <div className="mt-8 bg-white rounded-xl shadow-md overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex items-center gap-2 p-4">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-900">{t('deck_detail.detailed_analysis')}</h3>
            </div>
            <p className="px-4 pb-4 text-sm text-gray-600">
              {t('deck_detail.analysis_description')}
            </p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Carte des Types */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2 p-4">
                    <Swords className="w-5 h-5 text-indigo-600" />
                    <h4 className="text-base font-medium text-gray-900">{t('deck_detail.type_distribution')}</h4>
                  </div>
                </div>
                <div className="p-4 space-y-2">
                  {deck.cards && Object.entries(
                    deck.cards.reduce((acc, card) => {
                      const type = card.card?.type || t('deck_detail.unspecified');
                      acc[type] = (acc[type] || 0) + card.quantity;
                      return acc;
                    }, {} as Record<string, number>)
                  ).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-indigo-500" />
                        <span className="text-gray-700">{translateEnergyType(type, lang as 'fr' | 'en')}</span>
                      </div>
                      <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full text-sm">
                        {count} {t('deck_detail.cards', { count })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Carte des Statistiques */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2 p-4">
                    <Trophy className="w-5 h-5 text-indigo-600" />
                    <h4 className="text-base font-medium text-gray-900">{t('deck_detail.general_stats')}</h4>
                  </div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <ScrollText className="w-4 h-4 text-indigo-500" />
                      <span className="text-gray-700">{t('deck_detail.total_cards')}</span>
                    </div>
                    <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full text-sm">{totalCards}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-4 h-4 text-indigo-500" />
                      <span className="text-gray-700">{t('deck_detail.unique_cards')}</span>
                    </div>
                    <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full text-sm">{deck.cards?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-indigo-500" />
                      <span className="text-gray-700">{t('deck_detail.average_rating')}</span>
                    </div>
                    <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full text-sm">{(rating / 20).toFixed(1)}/5</span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-indigo-500" />
                      <span className="text-gray-700">{t('deck_detail.vote_count')}</span>
                    </div>
                    <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full text-sm">{totalVotes}</span>
                  </div>
                </div>
              </div>

              {/* Carte des Raretés */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2 p-4">
                    <Sparkles className="w-5 h-5 text-indigo-600" />
                    <h4 className="text-base font-medium text-gray-900">{t('deck_detail.rarity_distribution')}</h4>
                  </div>
                </div>
                <div className="p-4 space-y-2">
                  {deck.cards && Object.entries(
                    deck.cards.reduce((acc, card) => {
                      const rarity = card.card?.rarity || t('deck_detail.unspecified');
                      acc[rarity] = (acc[rarity] || 0) + card.quantity;
                      return acc;
                    }, {} as Record<string, number>)
                  ).map(([rarity, count]) => (
                    <div key={rarity} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-indigo-500" />
                        <span className="text-gray-700">{rarity}</span>
                      </div>
                      <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full text-sm">
                        {count} {t('deck_detail.cards', { count })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Section des sets avec plus d'informations */}
            <div className="mt-6">
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2 p-4">
                    <Box className="w-5 h-5 text-indigo-600" />
                    <h4 className="text-base font-medium text-gray-900">{t('deck_detail.set_distribution')}</h4>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {deck.cards && Object.entries(
                      deck.cards.reduce((acc, card) => {
                        const setId = card.card?.set;
                        if (!setId) return acc; // Ignorer les cartes sans set
                        
                        const setInfo = getSetInfo(setId);
                        const key = setInfo ? setId : 'unknown';
                        
                        if (!acc[key]) {
                          acc[key] = {
                            count: 0,
                            cards: new Set(),
                            setInfo: setInfo || {
                              display_name: t('deck_detail.unknown_set'),
                              name: 'unknown'
                            }
                          };
                        }
                        acc[key].count += card.quantity;
                        acc[key].cards.add(card.card?.id);
                        return acc;
                      }, {} as Record<string, { count: number; cards: Set<string>; setInfo: any }>)
                    ).filter(([setId]) => setId !== 'unknown') // Filtrer les sets inconnus
                     .map(([setId, { count, cards, setInfo }]) => (
                      <div 
                        key={setId} 
                        className="flex flex-col p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex flex-col items-center gap-3 mb-4">
                          {setInfo?.img && (
                            <img
                              src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${setInfo.img}`}
                              alt={setInfo.display_name}
                              className="w-32 h-32 object-contain rounded-lg bg-white p-4 shadow-md hover:shadow-lg transition-shadow"
                            />
                          )}
                          <div className="text-center">
                            <span className="font-medium text-gray-900 text-lg">
                              {lang === 'en' ? (setInfo?.display_name_en || setInfo?.display_name) : setInfo?.display_name || t('deck_detail.unknown_set')}
                            </span>
                            {setInfo?.release_date && (
                              <p className="text-sm text-gray-500 mt-1">
                                {new Date(setInfo.release_date).getFullYear()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col text-sm text-gray-600 space-y-2">
                          <div className="flex justify-between items-center">
                            <span>{t('deck_detail.total_cards')}:</span>
                            <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full">
                              {count}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span>{t('deck_detail.unique_cards')}:</span>
                            <span className="font-medium text-gray-900 bg-indigo-50 px-2 py-1 rounded-full">
                              {cards.size}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Analyse globale */}
            <div className="mt-8 bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2 p-4">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  <h4 className="text-base font-medium text-gray-900">{t('deck_detail.global_analysis')}</h4>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-4 text-gray-700">
                  {/* Analyse de la composition */}
                  <p>
                    {t('deck_detail.composition_analysis', {
                      deckName: deck.name,
                      strategy: deck.cards?.length / totalCards > 0.8 ? 
                        t('deck_detail.diverse_approach') : 
                        deck.cards?.length / totalCards < 0.4 ? 
                        t('deck_detail.focused_strategy') :
                        t('deck_detail.balanced_approach'),
                      cardDistribution: deck.cards?.some(card => card.quantity >= 3) ?
                        t('deck_detail.multiple_copies') :
                        deck.cards?.every(card => card.quantity === 1) ?
                        t('deck_detail.all_unique') :
                        t('deck_detail.balanced_distribution')
                    })}
                  </p>

                  {/* Analyse des votes - conditionnelle selon le nombre de votes */}
                  {totalVotes === 0 ? (
                    <p>
                      {t('deck_detail.no_votes')}
                    </p>
                  ) : totalVotes < 2 ? (
                    <p>
                      {t('deck_detail.few_votes', {
                        votes: totalVotes,
                        rating: (rating / 20).toFixed(1)
                      })}
                    </p>
                  ) : totalVotes < 5 ? (
                    <p>
                      {t('deck_detail.some_votes', {
                        votes: totalVotes,
                        rating: (rating / 20).toFixed(1),
                        comment: rating > 60 ? t('deck_detail.encouraging') : t('deck_detail.needs_improvement')
                      })}
                    </p>
                  ) : (
                    <p>
                      {t('deck_detail.many_votes', {
                        votes: totalVotes,
                        rating: (rating / 20).toFixed(1),
                        reception: rating > 80 ? t('deck_detail.excellent_reception') :
                                  rating > 60 ? t('deck_detail.positive_reception') :
                                  rating > 40 ? t('deck_detail.mixed_reception') :
                                  t('deck_detail.needs_adjustments')
                      })}
                    </p>
                  )}

                  {/* Analyse des sets et raretés combinée */}
                  <p>
                    {(() => {
                      // Calcul des statistiques de rareté
                      const rarityCounts = deck.cards?.reduce((acc, card) => {
                        const rarity = card.card?.rarity;
                        // Ignorer les cartes sans rareté ou avec rareté "-"
                        if (!rarity || rarity === '-') return acc;
                        
                        acc[rarity] = (acc[rarity] || 0) + card.quantity;
                        return acc;
                      }, {} as Record<string, number>);
                      
                      const rarityKeys = Object.keys(rarityCounts || {});
                      const mainRarity = rarityKeys.length > 0 ? 
                        rarityKeys.reduce((a, b) => (rarityCounts[a] > rarityCounts[b] ? a : b), rarityKeys[0]) :
                        null;

                      return (
                        <div className="space-y-4">
                          {/* Section Sets */}
                          <div className="flex items-start gap-2">
                            <Box className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-medium text-gray-900">{t('deck_detail.sets_distribution')}: </span>
                              <span>
                                {t('deck_detail.deck_uses_cards', {
                                  count: Object.keys(deck.cards?.reduce((acc, card) => {
                                    const setId = card.card?.set;
                                    acc[setId] = true;
                                    return acc;
                                  }, {} as Record<string, boolean>) || {}).length,
                                  sets: deck.cards?.reduce((acc, card) => {
                                    const setId = card.card?.set;
                                    const setInfo = getSetInfo(setId);
                                    const setName = lang === 'en' ? 
                                      (setInfo?.display_name_en || setInfo?.display_name) : 
                                      setInfo?.display_name;
                                    if (setName && !acc.includes(setName)) {
                                      acc.push(setName);
                                    }
                                    return acc;
                                  }, [] as string[]).slice(0, 3).join(', '),
                                  more: deck.cards?.reduce((acc, card) => {
                                    const setId = card.card?.set;
                                    const setInfo = getSetInfo(setId);
                                    const setName = lang === 'en' ? 
                                      (setInfo?.display_name_en || setInfo?.display_name) : 
                                      setInfo?.display_name;
                                    if (setName && !acc.includes(setName)) {
                                      acc.push(setName);
                                    }
                                    return acc;
                                  }, [] as string[]).length > 3 ? t('deck_detail.and_others') : ''
                                })}
                              </span>
                            </div>
                          </div>

                          {/* Section Raretés */}
                          <div className="flex items-start gap-2">
                            <Sparkles className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                            <div>
                              <span className="font-medium text-gray-900">{t('deck_detail.rarity_distribution')}: </span>
                              <span>
                                {mainRarity ? (
                                  <>
                                    {t('deck_detail.majority_rarity', {
                                      rarity: mainRarity.toLowerCase()
                                    })}
                                    {rarityKeys.length > 1 && (
                                      <> {t('deck_detail.accompanied_by', {
                                        rarities: rarityKeys.filter(r => r !== mainRarity).map(r => r.toLowerCase()).join(t('deck_detail.rarity_separator'))
                                      })}</>
                                    )}
                                  </>
                                ) : (
                                  t('deck_detail.no_rarity_specified')
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmation
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('deck_detail.delete_deck')}
        message={t('deck_detail.delete_confirmation')}
        isDeleting={isDeleting}
      />
    </PageLayout>
  );
}